/**
 * MOM Service - API calls for Minutes of Meeting Module
 */

import { api } from './client';
import {
  MOM,
  MOMListItem,
  MOMForm,
  MOMAttendee,
  MOMAttendeeForm,
  MOMActionItem,
  MOMActionItemForm,
  MOMActionItemComment,
  MOMActionItemCommentForm,
  MOMAttachment,
  MOMStatistics,
  PublicMOM,
  MOMApproval,
  MOMApprovalSummary,
} from '../../types/mom';

// ============================================
// Response Types
// ============================================

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface MOMListParams {
  page?: number;
  page_size?: number;
  search?: string;
  status?: string;
  project?: number;
  transaction?: number;
  client_profile?: number;
  created_by?: number;
  ordering?: string;
  meeting_date_after?: string;
  meeting_date_before?: string;
}

// ============================================
// MOM CRUD Operations
// ============================================

/**
 * Get paginated list of MOMs
 */
export const getMOMs = async (params?: MOMListParams): Promise<PaginatedResponse<MOMListItem>> => {
  const response = await api.get<PaginatedResponse<MOMListItem>>('/moms/', { params });
  return response.data;
};

/**
 * Get single MOM by ID
 */
export const getMOM = async (id: number): Promise<MOM> => {
  const response = await api.get<MOM>(`/moms/${id}/`);
  return response.data;
};

/**
 * Create new MOM
 */
export const createMOM = async (data: MOMForm): Promise<MOM> => {
  const response = await api.post<MOM>('/moms/', data);
  return response.data;
};

/**
 * Update existing MOM
 */
export const updateMOM = async (id: number, data: Partial<MOMForm>): Promise<MOM> => {
  const response = await api.patch<MOM>(`/moms/${id}/`, data);
  return response.data;
};

/**
 * Delete MOM (soft delete)
 */
export const deleteMOM = async (id: number): Promise<void> => {
  await api.delete(`/moms/${id}/`);
};

// ============================================
// MOM Actions
// ============================================

/**
 * Send MOM to attendees (changes status to 'sent')
 */
export const sendMOM = async (id: number): Promise<MOM> => {
  const response = await api.post<MOM>(`/moms/${id}/send/`);
  return response.data;
};

/**
 * Mark MOM as completed
 */
export const completeMOM = async (id: number): Promise<MOM> => {
  const response = await api.post<MOM>(`/moms/${id}/complete/`);
  return response.data;
};

/**
 * Export MOM as PDF
 */
export const exportMOMPdf = async (id: number, language: 'ar' | 'en' = 'ar'): Promise<Blob> => {
  const response = await api.get(`/moms/${id}/export-pdf/`, {
    params: { language },
    responseType: 'blob',
  });
  return response.data;
};

/**
 * Get MOM QR code
 */
export const getMOMQRCode = async (id: number): Promise<{ mom_id: string; qr_code: string; public_url: string }> => {
  const response = await api.get(`/moms/${id}/qr-code/`);
  return response.data;
};

/**
 * Duplicate MOM
 */
export const duplicateMOM = async (id: number): Promise<MOM> => {
  const response = await api.post<MOM>(`/moms/${id}/duplicate/`);
  return response.data;
};

/**
 * Get MOM statistics
 */
export const getMOMStatistics = async (): Promise<MOMStatistics> => {
  const response = await api.get<MOMStatistics>('/moms/statistics/');
  return response.data;
};

// ============================================
// MOM Approval Workflow Actions
// ============================================

/**
 * Approve MOM (client representative action)
 */
export const approveMOM = async (id: number): Promise<MOM> => {
  const response = await api.post<MOM>(`/moms/${id}/approve/`);
  return response.data;
};

/**
 * Reject MOM with reason (client representative action)
 */
export const rejectMOM = async (id: number, rejectionReason: string): Promise<MOM> => {
  const response = await api.post<MOM>(`/moms/${id}/reject/`, { rejection_reason: rejectionReason });
  return response.data;
};

/**
 * Resend MOM for approval after revisions (creator action)
 */
export const resendMOM = async (id: number): Promise<MOM> => {
  const response = await api.post<MOM>(`/moms/${id}/resend/`);
  return response.data;
};

/**
 * Get approval status for MOM
 */
export const getMOMApprovals = async (id: number): Promise<MOMApprovalSummary> => {
  const response = await api.get<MOMApprovalSummary>(`/moms/${id}/approvals/`);
  return response.data;
};

// ============================================
// Public MOM Access (no auth)
// ============================================

/**
 * Get public MOM by mom_id (for QR code access)
 */
export const getPublicMOM = async (momId: string): Promise<PublicMOM> => {
  const response = await api.get<PublicMOM>(`/moms/public/${momId}/`);
  return response.data;
};

// ============================================
// Attendee Operations
// ============================================

/**
 * Get attendees for a MOM
 */
export const getMOMAttendees = async (momId: number): Promise<MOMAttendee[]> => {
  const response = await api.get<MOMAttendee[]>(`/moms/${momId}/attendees/`);
  return response.data;
};

/**
 * Add attendee to MOM
 */
export const addMOMAttendee = async (momId: number, data: MOMAttendeeForm): Promise<MOMAttendee> => {
  const response = await api.post<MOMAttendee>(`/moms/${momId}/attendees/`, data);
  return response.data;
};

/**
 * Update attendee
 */
export const updateMOMAttendee = async (momId: number, attendeeId: number, data: Partial<MOMAttendeeForm>): Promise<MOMAttendee> => {
  const response = await api.patch<MOMAttendee>(`/moms/${momId}/attendees/${attendeeId}/`, data);
  return response.data;
};

/**
 * Remove attendee from MOM
 */
export const removeMOMAttendee = async (momId: number, attendeeId: number): Promise<void> => {
  await api.delete(`/moms/${momId}/attendees/${attendeeId}/`);
};

// ============================================
// Action Item Operations
// ============================================

/**
 * Get action items for a MOM
 */
export const getMOMActionItems = async (momId: number): Promise<MOMActionItem[]> => {
  const response = await api.get<MOMActionItem[]>(`/moms/${momId}/action-items/`);
  return response.data;
};

/**
 * Get single action item
 */
export const getMOMActionItem = async (momId: number, itemId: number): Promise<MOMActionItem> => {
  const response = await api.get<MOMActionItem>(`/moms/${momId}/action-items/${itemId}/`);
  return response.data;
};

/**
 * Create action item
 */
export const createMOMActionItem = async (momId: number, data: MOMActionItemForm): Promise<MOMActionItem> => {
  const response = await api.post<MOMActionItem>(`/moms/${momId}/action-items/`, data);
  return response.data;
};

/**
 * Update action item
 */
export const updateMOMActionItem = async (momId: number, itemId: number, data: Partial<MOMActionItemForm>): Promise<MOMActionItem> => {
  const response = await api.patch<MOMActionItem>(`/moms/${momId}/action-items/${itemId}/`, data);
  return response.data;
};

/**
 * Delete action item
 */
export const deleteMOMActionItem = async (momId: number, itemId: number): Promise<void> => {
  await api.delete(`/moms/${momId}/action-items/${itemId}/`);
};

/**
 * Change action item status
 */
export const changeActionItemStatus = async (
  momId: number,
  itemId: number,
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
): Promise<MOMActionItem> => {
  const response = await api.patch<MOMActionItem>(`/moms/${momId}/action-items/${itemId}/change-status/`, { status });
  return response.data;
};

// ============================================
// Action Item Comment Operations
// ============================================

/**
 * Get comments for an action item
 */
export const getActionItemComments = async (momId: number, itemId: number): Promise<MOMActionItemComment[]> => {
  const response = await api.get<MOMActionItemComment[]>(`/moms/${momId}/action-items/${itemId}/comments/`);
  return response.data;
};

/**
 * Add comment to action item
 */
export const addActionItemComment = async (
  momId: number,
  itemId: number,
  data: MOMActionItemCommentForm
): Promise<MOMActionItemComment> => {
  const response = await api.post<MOMActionItemComment>(`/moms/${momId}/action-items/${itemId}/comments/`, data);
  return response.data;
};

/**
 * Update comment
 */
export const updateActionItemComment = async (
  momId: number,
  itemId: number,
  commentId: number,
  data: Partial<MOMActionItemCommentForm>
): Promise<MOMActionItemComment> => {
  const response = await api.patch<MOMActionItemComment>(
    `/moms/${momId}/action-items/${itemId}/comments/${commentId}/`,
    data
  );
  return response.data;
};

/**
 * Delete comment
 */
export const deleteActionItemComment = async (momId: number, itemId: number, commentId: number): Promise<void> => {
  await api.delete(`/moms/${momId}/action-items/${itemId}/comments/${commentId}/`);
};

/**
 * Accept suggestion comment
 */
export const acceptSuggestion = async (momId: number, itemId: number, commentId: number): Promise<MOMActionItemComment> => {
  const response = await api.post<MOMActionItemComment>(
    `/moms/${momId}/action-items/${itemId}/comments/${commentId}/accept/`
  );
  return response.data;
};

/**
 * Reject suggestion comment
 */
export const rejectSuggestion = async (momId: number, itemId: number, commentId: number): Promise<MOMActionItemComment> => {
  const response = await api.post<MOMActionItemComment>(
    `/moms/${momId}/action-items/${itemId}/comments/${commentId}/reject/`
  );
  return response.data;
};

// ============================================
// Attachment Operations
// ============================================

/**
 * Get attachments for a MOM
 */
export const getMOMAttachments = async (momId: number): Promise<MOMAttachment[]> => {
  const response = await api.get<MOMAttachment[]>(`/moms/${momId}/attachments/`);
  return response.data;
};

/**
 * Upload attachment to MOM
 */
export const uploadMOMAttachment = async (
  momId: number,
  file: File,
  options?: {
    description?: string;
    is_client_visible?: boolean;
    include_in_pdf?: boolean;
  }
): Promise<MOMAttachment> => {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.description) {
    formData.append('description', options.description);
  }
  if (options?.is_client_visible !== undefined) {
    formData.append('is_client_visible', String(options.is_client_visible));
  }
  if (options?.include_in_pdf !== undefined) {
    formData.append('include_in_pdf', String(options.include_in_pdf));
  }

  const response = await api.post<MOMAttachment>(`/moms/${momId}/attachments/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Update attachment
 */
export const updateMOMAttachment = async (
  momId: number,
  attachmentId: number,
  data: {
    description?: string;
    is_client_visible?: boolean;
    include_in_pdf?: boolean;
  }
): Promise<MOMAttachment> => {
  const response = await api.patch<MOMAttachment>(`/moms/${momId}/attachments/${attachmentId}/`, data);
  return response.data;
};

/**
 * Delete attachment
 */
export const deleteMOMAttachment = async (momId: number, attachmentId: number): Promise<void> => {
  await api.delete(`/moms/${momId}/attachments/${attachmentId}/`);
};

// ============================================
// Field Configuration Operations
// ============================================

export interface MOMFieldConfiguration {
  id: number;
  scope: 'mom' | 'action_item' | 'attendee';
  scope_display: string;
  field_name: string;
  display_name: string;
  display_name_ar: string;
  is_enabled: boolean;
  is_required: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface MOMFieldConfigurationGrouped {
  mom: MOMFieldConfiguration[];
  action_item: MOMFieldConfiguration[];
  attendee: MOMFieldConfiguration[];
}

export interface MOMEnabledFields {
  mom: Record<string, boolean>;
  action_item: Record<string, boolean>;
  attendee: Record<string, boolean>;
}

/**
 * Get all field configurations
 */
export const getMOMFieldConfigurations = async (): Promise<MOMFieldConfiguration[]> => {
  const response = await api.get<MOMFieldConfiguration[]>('/moms/field-configurations/');
  return response.data;
};

/**
 * Get field configurations grouped by scope
 */
export const getMOMFieldConfigurationsGrouped = async (): Promise<MOMFieldConfigurationGrouped> => {
  const response = await api.get<MOMFieldConfigurationGrouped>('/moms/field-configurations/grouped/');
  return response.data;
};

/**
 * Get enabled fields grouped by scope
 */
export const getMOMEnabledFields = async (): Promise<MOMEnabledFields> => {
  const response = await api.get<MOMEnabledFields>('/moms/field-configurations/enabled/');
  return response.data;
};

/**
 * Update field configuration
 */
export const updateMOMFieldConfiguration = async (
  id: number,
  data: Partial<Pick<MOMFieldConfiguration, 'is_enabled' | 'is_required' | 'display_name' | 'display_name_ar' | 'display_order'>>
): Promise<MOMFieldConfiguration> => {
  const response = await api.patch<MOMFieldConfiguration>(`/moms/field-configurations/${id}/`, data);
  return response.data;
};

/**
 * Bulk update field configurations
 */
export const bulkUpdateMOMFieldConfigurations = async (
  configurations: Array<{
    id: number;
    is_enabled?: boolean;
    is_required?: boolean;
    display_order?: number;
    display_name?: string;
    display_name_ar?: string;
  }>
): Promise<{ status: string; updated: number; configurations: MOMFieldConfiguration[] }> => {
  const response = await api.post('/moms/field-configurations/bulk-update/', { configurations });
  return response.data;
};

/**
 * Reset field configurations to defaults
 */
export const resetMOMFieldConfigurations = async (): Promise<{ status: string; message: string }> => {
  const response = await api.post('/moms/field-configurations/reset-defaults/');
  return response.data;
};

// ============================================
// Helper Functions
// ============================================

/**
 * Download MOM PDF
 */
export const downloadMOMPdf = async (id: number, filename?: string, language: 'ar' | 'en' = 'ar'): Promise<void> => {
  const blob = await exportMOMPdf(id, language);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `mom_${id}_report.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// ============================================
// Auto-Approval Settings Operations
// ============================================

export interface MOMAutoApprovalSettings {
  is_enabled: boolean;
  days_limit: number;
  reminder_enabled: boolean;
  reminder_days_before: number;
  updated_at: string;
  updated_by: string | null;
}

/**
 * Get auto-approval settings
 */
export const getAutoApprovalSettings = async (): Promise<MOMAutoApprovalSettings> => {
  const response = await api.get<MOMAutoApprovalSettings>('/moms/auto-approval-settings/');
  return response.data;
};

/**
 * Update auto-approval settings (admin only)
 */
export const updateAutoApprovalSettings = async (
  data: Partial<Pick<MOMAutoApprovalSettings, 'is_enabled' | 'days_limit' | 'reminder_enabled' | 'reminder_days_before'>>
): Promise<MOMAutoApprovalSettings> => {
  const response = await api.put<MOMAutoApprovalSettings>('/moms/auto-approval-settings/', data);
  return response.data;
};

/**
 * Trigger auto-approval for expired MOMs (admin only)
 * This immediately runs the auto-approval task and returns the results.
 */
export interface TriggerAutoApprovalResult {
  success: boolean;
  processed: number;
  auto_approved: number;
  message: string;
}

export const triggerAutoApproval = async (): Promise<TriggerAutoApprovalResult> => {
  const response = await api.post<TriggerAutoApprovalResult>('/moms/trigger-auto-approval/');
  return response.data;
};

// ============================================
// Approval Dashboard Operations
// ============================================

export interface ApprovalDashboardItem {
  approval_id: number;
  mom_id: number;
  mom_number: string;
  title: string;
  title_ar?: string;
  meeting_date: string;
  client_profile: string | null;
  created_by: string | null;
  sent_at: string | null;
  deadline: string | null;
  days_remaining: number | null;
  hours_remaining: number | null;
  auto_approval_enabled: boolean;
  approval_source: 'client_rep' | 'attendee';
  approver_name: string | null;
  approver_email: string | null;
}

export interface ApprovalDashboardResponse {
  auto_approval_settings: {
    is_enabled: boolean;
    days_limit: number;
    reminder_enabled: boolean;
    reminder_days_before: number;
  };
  pending_count: number;
  external_pending_count: number;
  items: ApprovalDashboardItem[];
}

/**
 * Get approval dashboard data
 */
export const getApprovalDashboard = async (): Promise<ApprovalDashboardResponse> => {
  const response = await api.get<ApprovalDashboardResponse>('/moms/approval-dashboard/');
  return response.data;
};

// ============================================
// External Approval Operations (Public - No Auth)
// ============================================

export interface ExternalMOMData {
  mom_id: string;
  title: string;
  title_ar?: string;
  meeting_date: string;
  meeting_time?: string;
  location?: string;
  summary?: string;
  summary_ar?: string;
  client_profile?: string;
  created_by?: string;
  attendees: Array<{
    name: string;
    email?: string;
    role: string;
    represent_of?: string;
    attended: boolean;
  }>;
  action_items: Array<{
    sequence: number;
    description: string;
    description_ar?: string;
    responsible_name?: string;
    due_date?: string;
    status: string;
    priority: string;
  }>;
  approval_status: 'pending' | 'approved' | 'rejected';
  expires_at: string;
  attendee_name: string;
  attendee_email?: string;
  pdf_url: string;
  can_approve: boolean;
}

/**
 * Get external MOM data by token (public, no auth required)
 */
export const getExternalMOM = async (token: string): Promise<ExternalMOMData> => {
  const response = await api.get<ExternalMOMData>(`/moms/external/${token}/`);
  return response.data;
};

/**
 * Submit external approval (public, no auth required)
 */
export const submitExternalApproval = async (
  token: string,
  action: 'approve' | 'reject',
  rejectionReason?: string
): Promise<{ status: string; message: string }> => {
  const data: { action: string; rejection_reason?: string } = { action };
  if (action === 'reject' && rejectionReason) {
    data.rejection_reason = rejectionReason;
  }
  const response = await api.post<{ status: string; message: string }>(`/moms/external/${token}/`, data);
  return response.data;
};

/**
 * Download external MOM PDF by token (public, no auth required)
 */
export const downloadExternalMOMPdf = async (token: string, language: 'ar' | 'en' = 'ar'): Promise<void> => {
  const response = await api.get(`/moms/external/${token}/pdf/`, {
    params: { lang: language },
    responseType: 'blob',
  });
  const blob = response.data;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `mom_report.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * AI audio extraction — upload a meeting recording, poll until the
 * transcription + extraction job finishes, return the MOM prefill payload.
 *
 * Async job pattern (matches proposalService.analyzeTemplateFile): the POST
 * returns 202 + job_id in ~2s in prod; the pipeline (Whisper + Claude) runs
 * server-side via Cloud Tasks so no single HTTP round-trip outlives
 * Cloudflare's ~100s edge timeout. In local dev the dispatch runs inline,
 * so the start POST itself blocks — hence its generous timeout.
 */
export const extractMOMFromAudio = async (
  file: File,
  onUploadProgress?: (progressEvent: any) => void
): Promise<{ success: boolean; status?: string; data?: any; transcript?: string; error?: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  const startResp = await api.post('/moms/audio-extract/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
    timeout: 1200000,
  });
  const jobId: string | undefined = startResp.data?.job_id;
  if (!jobId) {
    return startResp.data;
  }

  const POLL_INTERVAL_MS = 3000;
  const MAX_WAIT_MS = 15 * 60 * 1000;
  const deadline = Date.now() + MAX_WAIT_MS;

  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    const poll = await api.get(`/moms/audio-extract/jobs/${jobId}/`, { timeout: 30000 });
    const { status: jobStatus } = poll.data || {};
    if (jobStatus === 'succeeded' || jobStatus === 'failed') {
      return poll.data;
    }
  }
  return { success: false, error: 'Extraction timed out. Please try again.' };
};
